import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Global keyboard shortcuts for power users.
 *
 * Sequences:  g d → Dashboard, g s → Subscribers, etc.
 * Singles:    ? → Help, n → New (context-aware), / → Focus search
 *
 * Disabled when focus is in an input/textarea/select or a modal is open.
 */
export function useKeyboardShortcuts(onOpenHelp: () => void) {
  const navigate = useNavigate();
  const location = useLocation();
  const bufferRef = useRef('');
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const isInputFocused = useCallback(() => {
    const tag = document.activeElement?.tagName?.toLowerCase();
    const isEditable = document.activeElement?.getAttribute('contenteditable') === 'true';
    const isInput = tag === 'input' || tag === 'textarea' || tag === 'select';
    return isInput || isEditable;
  }, []);

  const isModalOpen = useCallback(() => {
    return !!document.querySelector('[role="dialog"], [role="alertdialog"]');
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if input focused, modal open, or modifier held (except shift for ?)
      if (isInputFocused() || isModalOpen()) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const key = e.key;

      // ─── Single-key shortcuts ───

      // ? → Open shortcuts help
      if (key === '?') {
        e.preventDefault();
        onOpenHelp();
        return;
      }

      // / → Focus search (open Command Palette)
      if (key === '/') {
        e.preventDefault();
        window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
        return;
      }

      // n → New (context-aware based on current page)
      if (key === 'n') {
        e.preventDefault();
        const path = location.pathname;
        if (path.startsWith('/subscribers')) {
          navigate('/subscribers?action=new');
        } else if (path.startsWith('/plans')) {
          navigate('/plans?action=new');
        } else if (path.startsWith('/tickets')) {
          navigate('/tickets?action=new');
        } else if (path.startsWith('/olt')) {
          navigate('/olt?action=new');
        } else if (path.startsWith('/nas')) {
          navigate('/nas?action=new');
        }
        return;
      }

      // ─── Two-key sequences (g + letter) ───
      // Buffer the first key, wait for second within 800ms

      if (/^[a-z]$/.test(key)) {
        clearTimeout(timerRef.current);
        bufferRef.current += key;

        // Check for 2-char sequences
        if (bufferRef.current.length >= 2) {
          const seq = bufferRef.current.slice(-2);
          bufferRef.current = '';

          const routes: Record<string, string> = {
            gd: '/dashboard',
            gs: '/subscribers',
            gb: '/billing',
            gp: '/plans',
            gn: '/nas',
            go: '/olt',
            gc: '/noc',
            gt: '/tickets',
            gr: '/reports',
            gl: '/logs',
            ge: '/settings',
          };

          const target = routes[seq];
          if (target) {
            e.preventDefault();
            navigate(target);
            return;
          }
        }

        // Clear buffer after timeout
        timerRef.current = setTimeout(() => {
          bufferRef.current = '';
        }, 800);
      } else {
        // Non-letter key resets the buffer
        bufferRef.current = '';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timerRef.current);
    };
  }, [navigate, location.pathname, onOpenHelp, isInputFocused, isModalOpen]);
}
